import { throttle } from './utils.js';
import { GAME_TYPE_QUICK_CLICK, GAME_TYPE_CAMPAIGN_CLICK, GAME_TYPE_CAMPAIGN_ROLLING } from '../constants/gameTypes.js';
import { achievementTests } from '../testing/achievementTests.js';

/**
 * Wires up all global event listeners for UI interactions.
 * @param {App} app - The main application instance.
 */
export function setupEventListeners(app) {
    // One-time listener to unlock audio context on first user interaction.
    document.body.addEventListener('click', () => {
        app.audioManager.unlockAudio();
    }, { once: true });
    
    // New Game Hub button
    app.dom.buttons.newGameHub.addEventListener('click', throttle(() => {
        app.ui.showPrompt('newGameHub');
    }));

    app.dom.buttons.guide.addEventListener('click', throttle(async () => {
        await app.ui.setupGuidePage(
            app.biomesConfig,
            app.baseConfig,
            app.masterItemsConfig,
            app.campaignManager.levels,
            app.gameOptions
        );
        app.ui.showPage('guide');
    }));

    app.dom.buttons.testCases.addEventListener('click', throttle(() => {
        app.ui.setupTestCasesPage(
            achievementTests,
            async (testId) => {
                const testCase = achievementTests.find(t => t.id === testId);
                if (testCase) return await app.testRunner.runTest(testCase);
            },
            app.audioManager.config,
            (soundKey) => app.audioManager.play(soundKey)
        );
    }));

    app.dom.buttons.achievements.addEventListener('click', throttle(() => {
        app.ui.pageManager.showAchievementsPage();
    }));

    app.dom.buttons.about.addEventListener('click', throttle(() => {
        app.ui.showAboutPage();
    }));

    app.dom.buttons.settings.addEventListener('click', throttle(async () => {
        await app.ui.setupSettingsPage();
        app.ui.populateSettingsPage();
        app.ui.showPage('settings');
    }));

    app.dom.buttons.testCasesBackToGame.addEventListener('click', throttle(() => app.ui.showPage('game')));
    app.dom.buttons.achievementsBack.addEventListener('click', throttle(() => app.ui.showPage('game')));
    app.dom.buttons.saveGame.addEventListener('click', throttle(() => {
        console.log('isCampaignLevel:', app.game.gameState.isCampaignLevel);
        console.log('gameMode:', app.game.gameState.activeGameConfig.gameMode);
        let saveType;
        if (app.game.gameState.isCampaignLevel) {
            if (app.game.gameState.activeGameConfig.gameMode === 'rolling') {
                saveType = GAME_TYPE_CAMPAIGN_ROLLING;
            } else {
                saveType = GAME_TYPE_CAMPAIGN_CLICK;
            }
        } else {
            saveType = GAME_TYPE_QUICK_CLICK;
        }
        app.game.saveGame(saveType);
    }));
    app.dom.buttons.nextLevel.addEventListener('click', throttle(() => app.game.advanceToNextLevel()));
    app.dom.buttons.startGame.addEventListener('click', throttle(() => {
        const newSettings = app.ui.quickGameSelectedSettings;
        app.deleteGame(GAME_TYPE_QUICK_CLICK);
        app.configManager.setGridSize(newSettings.gridSize);
        app.configManager.setBiome(newSettings.biomeKey);
        app.configManager.setPathLength(newSettings.pathLength);
        app.configManager.setLimitedTurns(newSettings.isLimitedTurnsMode);
        app.configManager.setAnimalsEnabled(newSettings.isAnimalsEnabled);
        app.configManager.setDisastersEnabled(newSettings.isDisastersEnabled);
        app.configManager.setRandomQueueEnabled(newSettings.isRandomQueueEnabled);
        app.configManager.setNpcsEnabled(newSettings.isNpcsEnabled);
        app.configManager.setStationaryObjectsEnabled(newSettings.isStationaryObjectsEnabled);
        app.configManager.setPowerupsEnabled(newSettings.isPowerupsEnabled);
        app.configManager.setSettlersModeEnabled(newSettings.isSettlersModeEnabled);
        app.configManager.setGameMode(newSettings.gameMode);
        app.biomeManager.applyBiome(newSettings.biomeKey);
        app.startNewQuickGame();
    }));

    app.dom.buttons.startCampaign.addEventListener('click', throttle(() => {
        const newSettings = app.ui.campaignSelectedSettings;
        app.deleteGame(GAME_TYPE_CAMPAIGN_CLICK);
        app.startNewCampaign(newSettings);
    }));

    app.dom.buttons.startMiniMerge.addEventListener('click', throttle(() => {
        const newSettings = app.ui.miniMergeSelectedSettings;
        app.startMiniMergeGame(newSettings);
    }));

    app.dom.buttons.campaignSettingsBack.addEventListener('click', throttle(() => {
        // Go back to the new game hub
        app.ui.showPrompt('newGameHub');
    }));

    app.dom.buttons.muteToggle.addEventListener('click', throttle(() => {
        app.audioManager.toggleMute();
    }));

    // Listener for the debug toggle button
    if (app.dom.buttons.debugToggle) {
        app.dom.buttons.debugToggle.addEventListener('click', throttle(() => {
            const debugPanel = app.dom.pages.debugPanelContainer;
            if (debugPanel) {
                debugPanel.classList.toggle('hidden');
            }
        }));
    }

    // Listen for an event from the Game to update the UI when save slots change.
    app.dom.appContainer.addEventListener('ui:saveSlotsChanged', () => {
        if (!app.dom.pages.settings.classList.contains('hidden')) {
            app.ui.populateSettingsPage();
        }
    });

    app.dom.appContainer.addEventListener('saveSlotsChanged', () => {
        if (!app.dom.pages.settings.classList.contains('hidden')) {
            app.ui.populateSettingsPage();
        }
    });

    app.dom.appContainer.addEventListener('goldchanged', (e) => {
        app.ui.updateGold(e.detail.newGold);
    });

    app.dom.appContainer.addEventListener('prompt:startNewQuickGame', () => {
        app.ui.showQuickGameSettings();
    });

    app.dom.appContainer.addEventListener('prompt:startNewCampaign', () => {
        app.ui.showCampaignSettings();
    });

    app.dom.appContainer.addEventListener('prompt:startNewRollingGame', () => {
        app.startNewRollingGame();
    });

    app.dom.appContainer.addEventListener('prompt:loadGameByType', (e) => {
        const savedSlot = e.detail.savedSlot;
        app.loadGameByType(savedSlot.type, savedSlot.level);
    });

    app.dom.appContainer.addEventListener('prompt:resetProfileConfirmed', () => {
        app.playerStatsManager.resetStats();
        app.ui.populateSettingsPage(); // Repopulate to re-attach listeners
        app.ui.showPage('settings'); // Stay on settings page
    });

    app.dom.appContainer.addEventListener('prompt:cancel', () => {
        app.ui.showPage('intro');
    });

    app.dom.appContainer.addEventListener('prompt:newGameHub:quick', () => {
        const hasSave = app.saveManager.getSaveSlotByType(GAME_TYPE_QUICK_CLICK);
        if (hasSave) {
            app.ui.showPrompt('newGamePrompt', { quickSave: hasSave });
        } else {
            app.ui.showQuickGameSettings();
        }
    });

    app.dom.appContainer.addEventListener('prompt:newGameHub:campaign', () => {
        const hasSave = app.saveManager.getSaveSlotByType(GAME_TYPE_CAMPAIGN_CLICK);
        if (hasSave) {
            app.ui.showPrompt('newCampaignPrompt', { campaignSave: hasSave });
        } else {
            app.ui.showCampaignSettings();
        }
    });

    app.dom.appContainer.addEventListener('prompt:newGameHub:rolling', () => {
        const hasSave = app.saveManager.getSaveSlotByType(GAME_TYPE_CAMPAIGN_ROLLING);
        if (hasSave) {
            // Create a new prompt config on the fly for rolling mode
            app.ui.showPrompt('newRollingCampaignPrompt', { rollingCampaignSave: hasSave });
        } else {
            // No save, so just start a new rolling campaign
            app.startNewRollingGame();
        }
    });

    app.dom.appContainer.addEventListener('prompt:newGameHub:miniMerge', () => {
        app.ui.showMiniMergeSettings();
    });

    app.dom.appContainer.addEventListener('prompt:autosave:continue', () => {
        app.loadLatestAutosave();
    });

    app.dom.appContainer.addEventListener('prompt:autosave:exit', () => {
        app.ui.showInitialPageOrPrompt();
    });

    app.eventEmitter.on('ui:deleteGame', ({ type, level }) => app.deleteGame(type, level));
    app.eventEmitter.on('ui:loadGame', ({ type, level }) => app.loadGameByType(type, level));

    // --- App-level Orchestration Listeners ---

    app.eventEmitter.on('game:advanceLevel', () => app.game.advanceToNextLevel());

    app.eventEmitter.on('game:startNextLevel', ({ levelIndex, settings, saveType }) => {
        app.startCampaignLevel(levelIndex, settings, saveType);
    });
  // Listen for the new save button in the tools area
    app.eventEmitter.on('ui:saveGame', () => {
        let saveType;
        if (app.game.gameState.isCampaignLevel) {
            saveType = app.game.gameState.activeGameConfig.gameMode === 'rolling' ? GAME_TYPE_CAMPAIGN_ROLLING : GAME_TYPE_CAMPAIGN_CLICK;
        } else {
            saveType = GAME_TYPE_QUICK_CLICK;
        }
        app.game.saveGame(saveType);
    });

    app.eventEmitter.on('ui:pageChanged', ({ pageKey }) => {
        if (pageKey === 'game') {
            app.game.startGameLoop();
            app.ui.updateGold(app.playerStatsManager.getGold());
        } else {
            app.game.stopGameLoop();
            app.audioManager.stop('background'); // Stop background music when leaving game page
            app.ui.removeRtsGui(); // Also remove the RTS GUI if we leave the game page
        }

        // Handle demo idle timer based on the current page.
        if (pageKey === 'intro') {
            app.demoManager.startIdleTimer(); // Start the timer on the intro page.
        } else {
            app.demoManager.resetIdleTimer(); // Stop it on any other page.
        }
    });
    // --- Intro Page Handler ---
    // Define the handler once so it can be correctly removed from both listeners.
    const introPageHandler = () => {
        // Only proceed if the intro page is actually visible.
        if (app.dom.pages.intro.classList.contains('hidden')) return;

        app.ui.showPrompt('newGameHub');
    };
    app.dom.pages.intro.addEventListener('click', introPageHandler);
    window.addEventListener('keydown', introPageHandler);

    const _setupScoreButton = (button, amount) => {
        if (button) {
            button.addEventListener('click', throttle(() => {
                app.eventEmitter.emit('debug:addScore', amount);
            }));
        }
    };
    _setupScoreButton(app.dom.buttons.addScore100, 100);
    _setupScoreButton(app.dom.buttons.addScore1000, 1000);
    _setupScoreButton(app.dom.buttons.addScore10000, 10000);

    if (app.dom.buttons.completeLevelBtn) {
        app.dom.buttons.completeLevelBtn.addEventListener('click', throttle(() => {
            app.game.advanceToNextLevel();
        }));
    }

    /**
     * Helper to set up a disaster trigger button.
     * @param {HTMLElement} button - The button element.
     * @param {string} disasterKey - The key of the disaster to trigger.
     */
    const _setupDisasterButton = (button, disasterKey) => {
        if (button) {
            button.addEventListener('click', throttle(() => {
                app.eventEmitter.emit('game:startDisaster', disasterKey);
            }));
        }
    };
    _setupDisasterButton(app.dom.buttons.triggerGodzillaBtn, 'godzilla');
    _setupDisasterButton(app.dom.buttons.triggerWhirlwindBtn, 'whirlwind');
    _setupDisasterButton(app.dom.buttons.triggerStormBtn, 'storm');
    // --- Weather Cheat Buttons ---
    const weatherButtons = [
        { id: 'trigger-rain-btn', key: 'rain' },
        { id: 'trigger-snow-btn', key: 'snow' },
        { id: 'trigger-sunny-btn', key: 'sunny' },
        { id: 'trigger-moon-btn', key: 'moon' },
        { id: 'trigger-fog-btn', key: 'foggy' },
        { id: 'trigger-cloudy-btn', key: 'cloudy' },
        { id: 'trigger-storm-weather-btn', key: 'storm' }
    ];

    weatherButtons.forEach(btnInfo => {
        const button = document.getElementById(btnInfo.id);
        if (button) {
            button.addEventListener('click', throttle(() => {
                if (app.game.weatherManager) {
                    app.game.weatherManager.forceStartWeather(btnInfo.key);
                }
            }));
        }
    });

    const stopWeatherBtn = document.getElementById('stop-weather-btn');
    if (stopWeatherBtn) {
        stopWeatherBtn.addEventListener('click', throttle(() => {
            if (app.game.weatherManager) {
                app.game.weatherManager.stopWeather(true);
            }
        }));
    }

    const triggerBirdsBtn = document.getElementById('trigger-birds-btn');
    if (triggerBirdsBtn) {
        triggerBirdsBtn.addEventListener('click', throttle(() => {
            if (app.game.ambientAnimalManager) {
                app.game.ambientAnimalManager.forceStartFlock();
            }
        }));
    }

    const pauseNpcsBtn = document.getElementById('pause-npcs-btn');
    if (pauseNpcsBtn) {
        pauseNpcsBtn.addEventListener('click', throttle(() => {
            let isPaused = false;
            if (app.game.npcManager) {
                app.game.npcManager.togglePause();
                // We can check the state of one manager to represent the whole system
                isPaused = app.game.npcManager.getIsPaused();
            }
            if (app.game.nonHumanNPCManager) {
                app.game.nonHumanNPCManager.togglePause();
            }
            pauseNpcsBtn.textContent = isPaused ? 'Resume NPCs' : 'Pause NPCs';
        }));
    }

    // --- World Cheat Buttons ---
    if (app.dom.buttons.regenerateWallsBtn) {
        app.dom.buttons.regenerateWallsBtn.addEventListener('click', throttle(() => {
            if (app.game.obstacleManager) {
                app.game.obstacleManager.initManager();
            }
        }));
    }
    if (app.dom.buttons.regenerateRoadsBtn) {
        app.dom.buttons.regenerateRoadsBtn.addEventListener('click', throttle(() => {
            if (app.game.roadManager) {
                app.game.roadManager.initManager();
            }
        }));
    }

    if (app.dom.buttons.kickstartNpcsBtn) {
        app.dom.buttons.kickstartNpcsBtn.addEventListener('click', throttle(() => {
            app.eventEmitter.emit('debug:kickstartNpcs');
        }));
    }

    // --- Global Keyboard Hotkeys ---
    document.addEventListener('keydown', (e) => {
        // Do not trigger hotkeys if an input field is focused
        const focusedTagName = e.target.tagName.toLowerCase();
        if (focusedTagName === 'input' || focusedTagName === 'textarea' || focusedTagName === 'select') {
            return;
        }

        if (app.game && app.game.gameState) {
            // Hotkey for spotting mode (S)
            if (e.key.toLowerCase() === 't') {
                e.preventDefault();
                app.ui.toggleSpottingMode();
            }

            // Hotkey for First-Person View (F)
            if (e.key.toLowerCase() === 'f') {
                e.preventDefault();
                app.game.toggleFirstPersonView();
            }

            // Emit a generic arrow key event for other modules to use (like FPV)
            // Also include WASD for FPV movement.
            if (app.game.isFpvMode) {
                // Also include +/- for FPV zooming.
                if (e.key.startsWith('Arrow') || ['w', 'a', 's', 'd', 'q', 'e', 'z', 'c', '+', '=', '-', 'i', 'k', 'u', 'j'].includes(e.key.toLowerCase())) {
                    e.preventDefault();
                    app.eventEmitter.emit('keydown:arrow', e);
                }
            }

            // Hotkeys for Rolling Grid mode
            // Only listen for these keys if we are in rolling mode.
            if (app.game.gameState.activeGameConfig?.gameMode === 'rolling' && !app.game.isFpvMode) {
                if (e.key === 'ArrowLeft') {
                    e.preventDefault();
                    app.game.gameController.moveCollector('left');
                    app.game.gameController.handleRollingTurn();
                } else if (e.key === 'ArrowRight') {
                    e.preventDefault();
                    app.game.gameController.moveCollector('right');
                    app.game.gameController.handleRollingTurn();
                } else if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    app.game.gameController.handleRollingTurn();
                }
            }

            // Hotkey for RTS delete mode (D)
            if (e.key.toLowerCase() === 'd') {
                // Prevent default action (e.g., typing 'd' in an input field)
                e.preventDefault();
                // Toggle the delete mode in the game controller
                app.game.gameController.toggleDeleteMode();
            }
        }
    });

    const nextRollingTurnBtn = document.getElementById('next-rolling-turn-btn');
    if (nextRollingTurnBtn) {
        nextRollingTurnBtn.addEventListener('click', throttle(() => {
            app.game.gameController.handleRollingTurn();
        }));
    }
}